import { v4 as uuidv4 } from 'uuid';
import modelBaseV2 from "./model-base-v2.model";
import globalConfig from '../config/global-variables'
import profileModel from './profile.model';

const DEFAULT_CREDENTIALS = {
  userID: -1,
  username: "",
  agreementYN: "",
  loginStatus: -1,
  country: 0,
  language: "",
  languageName: "",
  privacyURL: "",
  eulaURL: "",
  managedCountryList: [],
  userFunctions: null,
  roleCode: -1,
  userFunctionUserRole: "",
  subsidiaryCode: "",
  admin: false
};

let sessionCheckPending = false;
let sessionValidated = false;

export const clearCredentials = () => {
  loginModel.credentials = structuredClone(DEFAULT_CREDENTIALS);
  loginModel.profileDetails = null;
  loginModel.currentSession = null;
  sessionValidated = false;
  localStorage.removeItem('mcs_language');
  localStorage.removeItem('mcs_country');
};

const setCredentials = (loginDetails) => {
  loginModel.credentials.agreementYN = loginDetails.agreementYN;
  if (loginDetails.agreementYN === 'Y') sessionStorage.setItem('mcs_agreementYN', 'Y');
  loginModel.credentials.privacyURL = loginDetails.privacyPolicyUrl;
  loginModel.credentials.eulaURL = loginDetails.eulaUrl;
  loginModel.credentials.state = loginDetails.state;
  loginModel.credentials.loginType = loginDetails.loginType;
  loginModel.currentSession = uuidv4();
  sessionValidated = true;
};

const populateCredentialsFromProfile = (result) => {
  const fieldMap = {
    userID: 'userID', userName: 'username', countryCode: 'country',
    userRoleCode: 'roleCode', languageCode: 'language',
    languageName: 'languageName', userFunctionRoleCode: 'userFunctionUserRole',
    subsidiaryCode: 'subsidiaryCode'
  };
  for (const [profileKey, credKey] of Object.entries(fieldMap)) {
    loginModel.credentials[credKey] = result[profileKey];
  }
  loginModel.credentials.admin = result.adminYN === 'Y';
  loginModel.credentials.agreementYN = result.agreementYN || loginModel.credentials.agreementYN;
  if (loginModel.credentials.agreementYN === 'Y') sessionStorage.setItem('mcs_agreementYN', 'Y');
  loginModel.profileDetails = result;
};

const getClientIp = () => {
  return modelBaseV2.getPublic(`${globalConfig.ADSSO_API_URL}/client/ip`, {}, true)
    .catch((error) => {
      console.warn("Failed to retrieve client IP, using default 0.0.0.0. Error:", error);
      return { ip: "0.0.0.0" };
    })
    .then((client) => {
      return (client && client.ip) ? client.ip : "0.0.0.0";
    });
};


const loginModel = {
  credentials: structuredClone(DEFAULT_CREDENTIALS),
  profileDetails: null,
  currentSession: null,

  checkSession: function () {
    sessionCheckPending = true;

    return profileModel.getProfile()
      .then((result) => {
        sessionCheckPending = false;
        if (result && result.userID) {
          populateCredentialsFromProfile(result);
          sessionValidated = true;
          return true;
        } else {
          clearCredentials();
          return false;
        }
      })
      .catch(() => {
        sessionCheckPending = false;
        clearCredentials();
        return false;
      });
  },

  setCountry: function (countryCode) {
    this.credentials.country = countryCode;
    localStorage.setItem('mcs_country', countryCode);
  },
  setLanguage: function (languageCode, languageName) {
    this.credentials.language = languageCode;
    this.credentials.languageName = languageName;
    localStorage.setItem('mcs_language', JSON.stringify({ code: languageCode, name: languageName }));
  },
  login: function (userId, password) {
    const params = {
      userID: userId,
      password: password,
    };

    return getClientIp()
      .then((clientIp) => {
        return modelBaseV2.postPublic("/api/account/auth", { body: params, headers: { "X-Client-Ip": clientIp } }, true)
          .then((result) => {
            if (!result || result.loginStatus != 0) {
              throw result?.statusMessage || 'Please contact administrator';
            }
            setCredentials(result);
            return result;
          })
      }).catch((error) => {
        console.error("Login Error: ", error);
        if (typeof error === 'string' || error instanceof String) {
          throw error || "Unexpected error";
        } else {
          throw "Unexpected error";
        }
      });
  },

  getUsersViaSamsungAccount: function (body) {
    return modelBaseV2.postPublic("/api/account/login/samsung-account/users", { body, withCredentials: true, }, true)
  },

  loginWithSpecificUserID: function (body) {
    return getClientIp()
      .then((clientIp) => {
        return modelBaseV2.postPublic("/api/account/login/samsung-account/userID", {
          body,
          withCredentials: true,
          headers: { "X-Client-Ip": clientIp }
        }, true)
          .then((result) => {
            if (!result || result.loginStatus != 0) {
              throw result?.statusMessage || 'Please contact administrator';
            }
            setCredentials(result);
            return result;
          })
      }).catch((error) => {
        if (typeof error === 'string' || error instanceof String) {
          throw error || "Unexpected error";
        } else {
          throw "Unexpected error";
        }
      });
  },

  loginADSSO: function (guid, userID = "", pid = '') {
    const params = { GUID: guid };

    if (userID) params.userID = userID;
    if (pid) params.pid = pid;

    return getClientIp()
      .then((clientIp) => {
        return modelBaseV2.postPublic("/api/account/auth/sso", { body: params, headers: { "X-Client-Ip": clientIp } }, true)
          .then((result) => {
            if (!result || result.loginStatus != 0) {
              throw result?.statusMessage || "Unexpected error";
            }

            setCredentials(result);

            return {
              isMultipleUser: !!(result.userList?.length),
              userList: result.userList,
              agreementYN: result.agreementYN,
              mobileSuccessLoginLink: result.mobileSuccessLoginLink
            };
          })
      }).catch((error) => {
        console.error("Login Error: ", error);
        if (typeof error === 'string' || error instanceof String) {
          throw error || "Unexpected error";
        } else {
          throw "Unexpected error";
        }
      });
  },

  logout: function () {
    const body = {
      state: loginModel.credentials.state,
      loginType: loginModel.credentials.loginType
    }

    return modelBaseV2.post("/api/account/logout", { body })
      .then((result) => {
        clearCredentials();
        if (body.loginType === 'SA') {
          m.route.set(`/logout?signOutURL=${result.signOutURL}`)
        } else {
          m.route.set("/login")
        }
      })
  },

  userAgreement: function () {
    return modelBaseV2.post("/api/user/agreement")
      .then(() => {
        loginModel.credentials.agreementYN = 'Y';
        sessionStorage.setItem('mcs_agreementYN', 'Y');
      });
  },

  loginRocketchat: function () {
    return modelBaseV2.post("/api/user/rocketchat/login")
  },
  
  hasAgreedAllAgreements: function () {
    return this.credentials?.agreementYN === 'Y' || sessionStorage.getItem('mcs_agreementYN') === 'Y';
  },

  isAuthenticated: function () {
    if (sessionCheckPending) return undefined;
    return sessionValidated;
  }
};

export default loginModel;
